import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import { createLogger } from 'redux-logger';
import thunkMiddleware from 'redux-thunk';
import { setupListeners } from '@reduxjs/toolkit/query';

import * as storage from 'redux-storage';
import createEngine from 'redux-storage-engine-localstorage';

import localStorageFilter from '../helpers/reduxLocalStorageFilter';
import rootReducer from './reducers';
import loggerConfig from '../config/logger';
import { __DEBUG__ } from '../config/constants';
import { api } from './api-generated';

const configureStore = (history) => {
  let initialStoreState = {};

  if (typeof window !== 'undefined' && window.__STATE_HYDRATE__) {
    initialStoreState = window.__STATE_HYDRATE__;
  }

  const enhancers = [];
  const middlewares = [
    thunkMiddleware,
    routerMiddleware(history),
    api.middleware,
  ];

  let devtools;

  if (__DEBUG__) {
    console.log('INITIAL STORE', initialStoreState);

    devtools = (
      typeof window !== 'undefined'
      && typeof window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ === 'function'
      && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ actionsBlacklist: [] })
    );

    if (devtools) {
      console.info('[setup] ✓ Enabling Redux DevTools Extension');
    }

    console.info('[setup] ✓ Enabling state logger');
    const loggerMiddleware = createLogger({
      level: 'info',
      collapsed: true,
      predicate: (getState, action) => {
        if (loggerConfig.blacklist.indexOf(action.type) !== -1) {
          return false;
        }

        return true;
      },
    });
    middlewares.push(loggerMiddleware);
  }

  // Provide the state keys to persist.
  // See: https://www.npmjs.com/package/redux-storage-decorator-filter
  const localStorageWhitelist = ['debug', 'popupAnouncement'];

  const storageReducer = storage.reducer(rootReducer);
  const storageEngine = localStorageFilter(createEngine(process.env.APP_NAME), localStorageWhitelist);
  const storageMiddleware = storage.createMiddleware(storageEngine);
  middlewares.push(storageMiddleware);


  const composedEnhancers = devtools || compose;
  const storeEnhancers = composedEnhancers(
    applyMiddleware(...middlewares),
    ...enhancers,
  );

  const store = createStore(
    storageReducer,
    initialStoreState,
    storeEnhancers,
  );

  // Use the provided storage loader to load the local storage in to the store.
  const storageLoader = storage.createLoader(storageEngine);
  storageLoader(store);

  // Enable Webpack hot module replacement for reducers.
  if (module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(rootReducer(history));
    });
  }

  // Required for refetchOnFocus/refetchOnReconnect behaviors from rtk-query.
  // See `setupListeners` docs - takes an optional callback as the 2nd arg for customization.
  setupListeners(store.dispatch);

  return store;
};

export default configureStore;
